<template>
  <!--
    PageContainer
    - Purpose: Minimal, page-level wrapper used for layouts/views.
    - Dashboard views (Home, Conversations, most lists): keep defaults (max-w-7xl, gutters on, padded on) and drop content into the default slot with an optional top slot heading.
    - Single-card views (Login/auth flows): constrain with maxWidth (e.g. max-w-lg), disable padded when you handle spacing manually, and enable centerVertically to keep the card centered on short pages.
    - centerVertically: use when the page is a short form/empty state that should sit in the middle of the viewport; avoid on long, scrollable pages where content needs natural top alignment.
    - See `app/layout-primitives-usage.md` for canonical layout patterns and page-type recommendations.
    - No visual chrome (no background, no border) by default.
    - Provides max width, responsive horizontal gutters, optional vertical centering,
      optional inner padding, and header/footer slots.
  -->
  <div :class="rootClass" role="main">
    <!-- optional top slot (e.g. page heading / breadcrumbs) -->
    <div v-if="$slots.top" class="my-2">
      <slot name="top" />
    </div>

    <!-- main content area -->
    <div name="content" :class="contentClass">
      <slot />
    </div>

    <!-- optional footer slot -->
    <div v-if="$slots.footer" class="mt-8">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface PageContainerProps {
  maxWidth?: string
  gutters?: boolean
  padded?: boolean
  centerVertically?: boolean
  class?: string | string[] | Record<string, boolean>
}

const props = withDefaults(defineProps<PageContainerProps>(), {
  maxWidth: 'max-w-7xl',
  gutters: true,
  padded: true,
  centerVertically: false,
  class: '',
})

const rootClass = computed(() => {
  // base outer wrapper: controls width and horizontal centering
  const base = [
    'w-full',
    'mx-auto',
    props.maxWidth,
    // apply responsive gutters unless explicitly disabled
    props.gutters ? 'px-container sm:px-6 lg:px-8' : '',
  ]

  // if the consumer wants vertical centering, make the wrapper a flex column full height
  if (props.centerVertically) {
    base.push('min-h-screen', 'flex', 'flex-col', 'justify-center')
  }

  // merge any extra classes the user supplied
  if (Array.isArray(props.class)) {
    base.push(...props.class)
  } else if (typeof props.class === 'string') {
    base.push(props.class)
  } else if (props.class && typeof props.class === 'object') {
    base.push(...Object.entries(props.class).filter(([, v]) => v).map(([k]) => k))
  }

  return base.filter(Boolean).join(' ')
})

const contentClass = computed(() => {
  const arr = ['w-full']
  if (props.padded) arr.push('py-4')
  return arr.join(' ')
})
</script>

<style scoped>
/* intentionally left blank — styling is driven by Tailwind classes */
</style>
